import React, { useState, useEffect, useCallback } from "react";
import { Header } from "build-ts/metadata";
import { getScheduledJobRunsByJobId } from "services/ScheduleServices";
import AnalysisRunsViewer from "../components/AnalysisRunsViewer";

const PAGE_SIZE = 10;

interface AnalysisRunsViewerContainerProps {
  selectedJob: Header | undefined;
  /** Whether to hide the run details on the Analysis Run Card. */
  hideRunDetails?: boolean;
}

const AnalysisRunsViewerContainer: React.FC<AnalysisRunsViewerContainerProps> = ({
  selectedJob,
  hideRunDetails = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [jobRuns, setJobRuns] = useState<Header[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

  const fetchJobRuns = useCallback(async (jobId: string, page: number) => {
    setIsLoading(true);
    const result = await getScheduledJobRunsByJobId(jobId, PAGE_SIZE, page);
    if (result) {
      setJobRuns(result.headers);
      setTotalRows(result.totalRows);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (selectedJob?.id) {
      setPageNumber(1);
      fetchJobRuns(selectedJob.id, 1);
    }
  }, [selectedJob?.id, fetchJobRuns]);

  const handlePrevPage = useCallback(() => {
    if (pageNumber <= 1 || !selectedJob?.id) return;
    const newPage = pageNumber - 1;
    setPageNumber(newPage);
    fetchJobRuns(selectedJob.id, newPage);
  }, [pageNumber, selectedJob?.id, fetchJobRuns]);

  const handleNextPage = useCallback(() => {
    if (pageNumber >= totalPages || !selectedJob?.id) return;
    const newPage = pageNumber + 1;
    setPageNumber(newPage);
    fetchJobRuns(selectedJob.id, newPage);
  }, [pageNumber, totalPages, selectedJob?.id, fetchJobRuns]);

  return (
    <AnalysisRunsViewer
      isLoading={isLoading}
      jobRuns={jobRuns}
      pageNumber={pageNumber}
      totalPages={totalPages}
      onPrevPage={handlePrevPage}
      onNextPage={handleNextPage}
      hideRunDetails={hideRunDetails}
    />
  );
};

export default AnalysisRunsViewerContainer;
